import { Button } from '../catalyst/button'
import { Subheading } from '../catalyst/heading'
import { ButtonGroup, ButtonGroupItem } from './button-group'
import type { ChecklistItem, ChecklistSection } from './canadagap-checklists'
import type { ChecklistAnswer } from './types'

export type ChecklistValue = Partial<Record<string, ChecklistAnswer>>

const ANSWERS: { value: ChecklistAnswer; label: string; tone: 'emerald' | 'red' | 'zinc' }[] = [
  { value: 'ok', label: 'OK', tone: 'emerald' },
  { value: 'not_ok', label: 'Issue', tone: 'red' },
  { value: 'na', label: 'N/A', tone: 'zinc' },
]

/**
 * A CanadaGAP checklist (Forms G, J): every item starts unanswered and is answered OK / Issue / N/A,
 * so a saved form is an explicit record of what was checked. "All OK" fills only the items of that
 * section still unanswered.
 */
export function ChecklistFieldset({
  sections,
  items,
  value,
  onChange,
}: {
  sections: ChecklistSection[]
  items: ChecklistItem[]
  value: ChecklistValue
  onChange: (value: ChecklistValue) => void
}) {
  return (
    <div className="space-y-6">
      {sections.map((section) => {
        const sectionItems = items.filter((i) => i.section === section.key)
        const unanswered = sectionItems.filter((i) => !value[i.key])
        return (
          <div key={section.key}>
            <div className="flex items-center justify-between gap-4">
              <Subheading level={3} className="!text-sm">
                {section.title}
              </Subheading>
              <Button
                plain
                disabled={unanswered.length === 0}
                onClick={() => onChange({ ...value, ...Object.fromEntries(unanswered.map((i) => [i.key, 'ok' as const])) })}
              >
                All OK
              </Button>
            </div>
            <ul className="mt-2 divide-y divide-zinc-950/5 dark:divide-white/5">
              {sectionItems.map((item) => (
                <li key={item.key} className="flex items-center justify-between gap-4 py-2">
                  <span className="text-sm text-zinc-700 dark:text-zinc-300">{item.label}</span>
                  <ButtonGroup aria-label={item.label} className="shrink-0">
                    {ANSWERS.map((a) => (
                      <ButtonGroupItem
                        key={a.value}
                        current={value[item.key] === a.value}
                        currentTone={a.tone}
                        onClick={() => onChange({ ...value, [item.key]: a.value })}
                      >
                        {a.label}
                      </ButtonGroupItem>
                    ))}
                  </ButtonGroup>
                </li>
              ))}
            </ul>
          </div>
        )
      })}
    </div>
  )
}
